import { useState, type KeyboardEvent } from 'react';
import { cn } from '../cn.js';
import { Icon } from './Icon.js';

export interface TagInputProps {
  label?: string;
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  maxTags?: number;
  className?: string;
}

/** A chip-based tag editor: type a tag and press Enter to add it. */
export function TagInput({
  label,
  value,
  onChange,
  placeholder,
  maxTags = 20,
  className,
}: TagInputProps): JSX.Element {
  const [draft, setDraft] = useState('');

  const addTag = (raw: string): void => {
    const tag = raw.trim();
    if (!tag) return;
    if (value.includes(tag)) {
      setDraft('');
      return;
    }
    if (value.length >= maxTags) return;
    onChange([...value, tag]);
    setDraft('');
  };

  const removeTag = (tag: string): void => {
    onChange(value.filter((t) => t !== tag));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addTag(draft);
    } else if (event.key === 'Backspace' && draft === '' && value.length > 0) {
      removeTag(value[value.length - 1] as string);
    }
  };

  return (
    <div className={cn('as-field', className)}>
      {label && (
        <span className="as-field__top">
          <span className="as-field__label">{label}</span>
        </span>
      )}
      <div className="as-taginput">
        {value.map((tag) => (
          <span key={tag} className="as-tag">
            {tag}
            <button
              type="button"
              className="as-tag__remove"
              aria-label={`Remove ${tag}`}
              onClick={() => removeTag(tag)}
            >
              <Icon name="close" size={12} />
            </button>
          </span>
        ))}
        <input
          className="as-taginput__input"
          value={draft}
          placeholder={value.length === 0 ? placeholder : undefined}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => addTag(draft)}
        />
      </div>
    </div>
  );
}
